import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Icon,
  useToast,
  OrderedList,
  ListItem,
  UnorderedList,
  Flex,
  Badge,
  Tooltip
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useAuthContext } from '../context/AuthContext';
import { updateUid, updateApiCredentials } from '../services/authService';
import { authAPI } from '../services/api';

// Keyframes
const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(-5deg); }
  66% { transform: translateY(-10px) rotate(5deg); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.5), 0 0 40px rgba(16, 185, 129, 0.3); }
  50% { box-shadow: 0 0 30px rgba(16, 185, 129, 0.7), 0 0 60px rgba(16, 185, 129, 0.4); }
`;

const slideInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AccountSetup = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuthContext();
  const toast = useToast();
  
  // Form states
  const [uid, setUid] = useState(user?.uid || '');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [apiPassphrase, setApiPassphrase] = useState('');
  
  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [showSecrets, setShowSecrets] = useState({ secret: false, passphrase: false });
  
  // Get user step from user object, default to 1
  const userStep = user?.step || 1;
  const userRole = user?.role || 0;
  
  // Force redirect for role 1 users
  useEffect(() => {
    if (userRole === 1) {
      navigate('/', { replace: true });
    }
  }, [userRole, navigate]);
  
  // Secondary check for role 1 users
  useEffect(() => {
    if (user && user.role === 1) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // UID verification check interval
  useEffect(() => {
    if (user?.uid && user?.step === 1) {
      const checkVerificationStatus = async () => {
        try {
          const response = await authAPI.getUser();
          
          if (response.data?.success && response.data?.data?.authenticated) {
            const updatedUser = response.data.data.user;
            
            if (updatedUser.step === 2 && user.step === 1) {
              setUser(updatedUser);
              
              toast({
                title: '✨ Verification Complete',
                description: 'Your Bitget UID has been verified. You can now proceed with API setup.',
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top',
                variant: 'solid',
              });
            }
          }
        } catch (error) {
          console.error('Error checking verification status:', error);
        }
      };
      
      checkVerificationStatus();
      const intervalId = setInterval(checkVerificationStatus, 60000);
      
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [user?.uid, user?.step, setUser, toast]);

  // Local storage sync
  const initialUidRef = useRef(null);
  
  useEffect(() => {
    if (user && user.uid && user.uid !== initialUidRef.current) {
      initialUidRef.current = user.uid;
      setUid(user.uid);
    }
  }, [user]);
  
  // UID form submission
  const handleUidSubmit = async (e) => {
    e.preventDefault();
    
    if (!uid || !/^\d+$/.test(uid)) {
      toast({
        title: '⚠️ Invalid UID',
        description: 'Please enter a valid UID number. Only numbers are allowed.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
        variant: 'solid',
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await updateUid(uid);
      
      if (response.success) {
        toast({
          title: '🎯 Success',
          description: 'Your UID has been successfully submitted for verification.',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
          variant: 'solid',
        });
        
        if (response.data?.user) {
          setUser(response.data.user);
          setUid(response.data.user.uid || uid);
        }
      } else {
        throw new Error(response.message || 'Failed to submit UID');
      }
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message || 'An error occurred while submitting your UID. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
        variant: 'solid',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // API form submission
  const handleApiSubmit = async (e) => {
    e.preventDefault();
    
    if (!apiKey || !apiSecret || !apiPassphrase) {
      toast({
        title: '⚠️ Missing Information',
        description: 'All API information is required. Please complete all fields.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
        variant: 'solid',
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await updateApiCredentials({
        api_key: apiKey,
        api_secret: apiSecret,
        api_passphrase: apiPassphrase
      });
      
      if (response.success) {
        toast({
          title: '🚀 Success',
          description: 'Your API credentials have been successfully verified. Redirecting...',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top',
          variant: 'solid',
        });
        
        if (response.data?.user) {
          setUser(response.data.user);
        }
        
        navigate('/', { replace: true });
      } else {
        throw new Error(response.message || 'Failed to save API credentials');
      }
    } catch (error) {
      toast({
        title: '🔌 Connection Failed',
        description: 'Please check your API credentials and permissions. Make sure you have enabled all required permissions.',
        status: 'error',
        duration: 7000,
        isClosable: true,
        position: 'top',
        variant: 'solid',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Paste functionality
  const pasteText = async (fieldName, numbersOnly = false) => {
    try {
      const text = await navigator.clipboard.readText();
      
      if (numbersOnly) {
        const numbersText = text.replace(/[^0-9]/g, '').substring(0, 15);
        if (numbersText) {
          setUid(numbersText);
          toast({
            title: '✅ Pasted',
            description: 'Content pasted successfully.',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
            variant: 'subtle',
          });
        }
      } else {
        const trimmedText = text.trim();
        if (trimmedText) {
          switch(fieldName) {
            case 'api_key':
              setApiKey(trimmedText);
              break;
            case 'api_secret':
              setApiSecret(trimmedText);
              break;
            case 'api_passphrase':
              setApiPassphrase(trimmedText);
              break;
            default:
              break;
          }
          toast({
            title: '✅ Pasted',
            description: 'Content pasted successfully.',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
            variant: 'subtle',
          });
        }
      }
    } catch (err) {
      toast({
        title: '❌ Error',
        description: 'Unable to access clipboard. Please paste manually using Ctrl+V.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
        variant: 'subtle',
      });
    }
  };
  
  // Toggle accordion
  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };
  
  return (
    <Box 
      minH="100vh" 
      display="flex" 
      flexDirection="column" 
      bg="linear-gradient(180deg, rgb(0, 0, 0) 0%, rgb(7, 35, 7) 100%)" 
      color="white"
      position="relative"
      overflow="hidden"
    >
      {/* Animated background elements */}
      <Box
        position="absolute"
        top="10%"
        left="5%"
        w={{ base: "150px", md: "300px" }}
        h={{ base: "150px", md: "300px" }}
        borderRadius="50%"
        bg="radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)"
        filter="blur(40px)"
        animation={`${float} 20s ease-in-out infinite`}
      />
      <Box
        position="absolute"
        bottom="20%"
        right="10%"
        w={{ base: "120px", md: "250px" }}
        h={{ base: "120px", md: "250px" }}
        borderRadius="50%"
        bg="radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)"
        filter="blur(40px)"
        animation={`${float} 20s ease-in-out infinite`}
        animationDelay="-7s"
      />
      
      {/* Header */}
      <Box py={{ base: 4, md: 6 }} borderBottom="1px solid" borderColor="whiteAlpha.100" position="relative" zIndex={1}>
        <Box maxW="container.lg" mx="auto" px={{ base: 4, md: 6 }}>
          <Flex align="center" justify="space-between">
            <HStack spacing={{ base: 2, md: 3 }}>
              <Box
                bgGradient="linear(135deg, rgb(40 185 16), rgb(24 79 38))"
                p={{ base: "2", md: "2.5" }}
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                overflow="hidden"
                animation={`${float} 6s ease-in-out infinite`}
                _hover={{ transform: 'scale(1.05)' }}
                transition="transform 0.3s"
                cursor="pointer"
                onClick={() => navigate('/')}
              >
                <img 
                  src="/assets/light_logo.png" 
                  alt="Wells" 
                  style={{
                    width: '24px',
                    height: '24px',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Heading size={{ base: "md", md: "lg" }} fontWeight="bold" letterSpacing="-0.02em">
                Wells<Text as="span" color="crypto.green.400">.</Text>
              </Heading>
            </HStack>
            
            <HStack spacing={2} display={{ base: 'none', md: 'flex' }}>
              <Text fontSize="sm" color="whiteAlpha.600">Account Setup</Text>
              <Text fontSize="sm" color="crypto.green.400">•</Text>
              <Text fontSize="sm" color="whiteAlpha.600">{user?.fullname || 'User'}</Text>
            </HStack>
          </Flex>
        </Box>
      </Box>
      
      <Box flex={1} py={{ base: 6, md: 10 }} position="relative" zIndex={1}>
        <Box maxW="container.md" mx="auto" px={{ base: 4, md: 6 }}>
          {/* Progress indicator */}
          <Box mb={{ base: 8, md: 12 }} position="relative" animation={`${slideInUp} 0.8s ease-out`}>
            <Flex justify="center" align="center" position="relative" px={{ base: 10, md: 20 }}>
              <Box 
                position="absolute" 
                left={{ base: "25%", md: "50%" }}
                transform={{ base: "translateX(-25%)", md: "translateX(-50%)" }}
                width={{ base: "100px", md: "200px" }}
                height="2px"
                bg={userStep >= 2 ? "linear-gradient(90deg, #10b981 0%, #059669 100%)" : "rgba(255, 255, 255, 0.1)"}
              />
              
              <Box position="relative" zIndex={2}>
                <Box 
                  w={{ base: "32px", md: "40px" }}
                  h={{ base: "32px", md: "40px" }}
                  borderRadius="50%"
                  bg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="600"
                  fontSize={{ base: "sm", md: "md" }}
                  boxShadow="0 0 20px rgba(16, 185, 129, 0.5)"
                >
                  <Text>1</Text>
                </Box>
                <Text 
                  position="absolute" 
                  top={{ base: "40px", md: "50px" }}
                  left="50%" 
                  transform="translateX(-50%)" 
                  fontSize={{ base: "2xs", md: "xs" }}
                  whiteSpace="nowrap" 
                  color="crypto.green.400"
                >
                  UID Verification
                </Text>
              </Box>
              
              <Box position="relative" zIndex={2} ml={{ base: "100px", md: "200px" }}>
                <Box 
                  w={{ base: "32px", md: "40px" }}
                  h={{ base: "32px", md: "40px" }}
                  borderRadius="50%"
                  bg={userStep === 2 ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "rgba(255, 255, 255, 0.1)"}
                  border={userStep < 2 ? "2px solid" : "none"}
                  borderColor="rgba(255, 255, 255, 0.2)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="600"
                  fontSize={{ base: "sm", md: "md" }}
                  animation={userStep === 2 ? `${pulse} 2s infinite` : "none"}
                  boxShadow={userStep === 2 ? "0 0 20px rgba(16, 185, 129, 0.5)" : "none"}
                >
                  <Text>2</Text>
                </Box>
                <Text 
                  position="absolute" 
                  top={{ base: "40px", md: "50px" }}
                  left="50%" 
                  transform="translateX(-50%)" 
                  fontSize={{ base: "2xs", md: "xs" }}
                  whiteSpace="nowrap" 
                  color={userStep === 2 ? "crypto.green.400" : "whiteAlpha.500"}
                >
                  API Setup
                </Text>
              </Box>
            </Flex>
          </Box>
          
          {/* Welcome message */}
          <VStack spacing={2} mb={{ base: 6, md: 10 }} textAlign="center" animation={`${slideInUp} 0.8s ease-out`} animationDelay="0.2s" opacity={0} sx={{ animationFillMode: 'forwards' }}>
            <Heading size={{ base: "xl", md: "2xl" }} fontWeight="bold" letterSpacing="-0.03em">
              {userStep === 1 ? 'Verify Your Account' : 'Connect Your API'}
            </Heading>
            <Text color="whiteAlpha.700" fontSize={{ base: "md", md: "lg" }} px={{ base: 4, md: 0 }}>
              {userStep === 1 
                ? 'Let\'s verify your Bitget account to enable AI-powered trading'
                : 'Final step: Connect your Bitget API for automated trading'}
            </Text>
          </VStack>
          
          {/* Step 1: UID Input */}
          {userStep === 1 && (
            <Box 
              p={{ base: 4, md: 8 }}
              bg="rgba(15, 27, 14, 0.6)"
              backdropFilter="blur(20px)"
              border="1px solid"
              borderColor="rgba(16, 185, 129, 0.2)"
              borderRadius={{ base: "16px", md: "20px" }}
              position="relative"
              overflow="hidden"
              transition="all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)"
              animation={`${slideInUp} 0.8s ease-out`}
              animationDelay="0.3s"
              opacity={0}
              sx={{ animationFillMode: 'forwards' }}
              _hover={{
                transform: { base: 'none', md: 'translateY(-5px)' },
                borderColor: 'rgba(16, 185, 129, 0.4)',
                boxShadow: { base: 'none', md: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(16, 185, 129, 0.2)' }
              }}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                height="1px"
                bg="linear-gradient(90deg, transparent 0%, rgba(16, 185, 129, 0.5) 50%, transparent 100%)"
                opacity={0}
                transition="opacity 0.3s ease"
                sx={{
                  '.premium-card:hover &': {
                    opacity: 1
                  }
                }}
              />
              
              <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                <HStack spacing={{ base: 3, md: 4 }} flexDirection={{ base: 'column', sm: 'row' }} alignItems={{ base: 'center', sm: 'flex-start' }}>
                  <Box
                    w={{ base: 10, md: 12 }}
                    h={{ base: 10, md: 12 }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="full"
                    bg="linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)"
                    position="relative"
                    animation={`${float} 6s ease-in-out infinite`}
                  >
                    <Icon viewBox="0 0 24 24" boxSize={{ base: 5, md: 6 }} color="crypto.green.400">
                      <path fill="currentColor" d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
                    </Icon>
                    <Box
                      position="absolute"
                      inset="-1px"
                      borderRadius="full"
                      border="2px solid"
                      borderColor="crypto.green.400"
                      opacity={0.3}
                      animation={`${pulse} 2s infinite`}
                    />
                  </Box>
                  <Box textAlign={{ base: 'center', sm: 'left' }}>
                    <Heading size={{ base: "md", md: "lg" }} fontWeight="semibold" letterSpacing="-0.02em">
                      Bitget UID Verification
                    </Heading>
                    <Text color="whiteAlpha.600" fontSize={{ base: "xs", md: "sm" }}>
                      Enter your unique Bitget identifier
                    </Text>
                  </Box>
                </HStack>
                
                {/* Status message for submitted UID */}
                {user && user.uid && (
                  <Box
                    p={4}
                    borderRadius="xl"
                    bg="linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    position="relative"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      top={0}
                      right={0}
                      width="100px"
                      height="100px"
                      bg="radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%)"
                      filter="blur(40px)"
                    />
                    <HStack align="start" spacing={3} position="relative">
                      <Icon viewBox="0 0 24 24" boxSize={5} color="purple.400" mt={0.5}>
                        <path fill="currentColor" d="M22 12h-4l-3 9L9 3l-3 9H2" />
                      </Icon>
                      <Box>
                        <Text fontWeight="semibold" color="purple.300" mb={1}>
                          UID Submitted: {user.uid}
                        </Text>
                        <Text fontSize="sm" color="whiteAlpha.700">
                          Our team is reviewing your application. You'll receive an email confirmation once verified.
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                )}
                
                {/* Important notice */}
                <Box
                  p={4}
                  borderRadius="xl"
                  bg="linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)"
                  border="1px solid"
                  borderColor="crypto.green.500"
                  borderOpacity={0.3}
                  position="relative"
                  overflow="hidden"
                >
                  <HStack align="start" spacing={3}>
                    <Icon viewBox="0 0 24 24" boxSize={5} color="crypto.green.400" mt={0.5}>
                      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" />
                    </Icon>
                    <Box>
                      <Text fontWeight="semibold" color="crypto.green.400" mb={1}>
                        Important Requirement
                      </Text>
                      <Text fontSize="sm" color="whiteAlpha.700">
                        You must be registered on Bitget using our referral code{' '}
                        <Badge 
                          colorScheme="green" 
                          px={2} 
                          py={0.5} 
                          borderRadius="md" 
                          fontSize="sm"
                          bg="crypto.green.500"
                          color="black"
                          fontWeight="bold"
                        >
                          wells1
                        </Badge>{' '}
                        to be eligible.
                      </Text>
                    </Box>
                  </HStack>
                </Box>
                
                <form onSubmit={handleUidSubmit}>
                  <VStack spacing={5}>
                    <Box w="full">
                      <Flex justify="space-between" mb={2} flexDirection={{ base: "column", sm: "row" }} gap={1}>
                        <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium" color="whiteAlpha.800">
                          Bitget UID
                        </Text>
                        <Tooltip 
                          label="Your Bitget User ID is a unique number that identifies your account on the exchange" 
                          placement="top"
                          bg="gray.900"
                          color="white"
                          borderRadius="md"
                          px={3}
                          py={2}
                        >
                          <Text fontSize="xs" color="crypto.green.400" cursor="help">
                            What is this?
                          </Text>
                        </Tooltip>
                      </Flex>
                      <VStack spacing={3} align="stretch" display={{ base: "flex", md: "none" }}>
                        <InputGroup 
                          size="lg" 
                          flex={1}
                          position="relative"
                          _focusWithin={{
                            boxShadow: '0 0 0 1px #10b981, 0 0 20px rgba(16, 185, 129, 0.4)'
                          }}
                        >
                          <InputLeftElement pointerEvents="none" pl={1}>
                            <Icon viewBox="0 0 24 24" boxSize={5} color="whiteAlpha.400">
                              <rect x="2" y="4" width="20" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                              <path d="M7 8h.01M12 8h.01M17 8h.01M7 12h.01M12 12h.01M17 12h.01M7 16h.01M12 16h.01M17 16h.01" stroke="currentColor" strokeWidth="2" />
                            </Icon>
                          </InputLeftElement>
                          <Input
                            value={uid}
                            onChange={(e) => setUid(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder="Enter your Bitget UID"
                            bg="blackAlpha.400"
                            border="1px solid"
                            borderColor="whiteAlpha.200"
                            _focus={{ 
                              borderColor: 'crypto.green.400',
                              bg: 'blackAlpha.600'
                            }}
                            _hover={{ borderColor: 'whiteAlpha.300' }}
                            color="white"
                            _placeholder={{ color: 'whiteAlpha.400' }}
                            fontSize="md"
                            pattern="[0-9]{1,15}"
                            maxLength="15"
                            inputMode="numeric"
                            isDisabled={isLoading}
                            h={{ base: "48px", md: "50px" }}
                            borderRadius="xl"
                            pl="50px"
                          />
                        </InputGroup>
                        <Button
                          w="full"
                          h="48px"
                          px={6}
                          onClick={() => pasteText('uid', true)}
                          bg="rgba(255, 255, 255, 0.05)"
                          border="1px solid"
                          borderColor="rgba(255, 255, 255, 0.1)"
                          _hover={{
                            bg: 'rgba(16, 185, 129, 0.1)',
                            borderColor: 'rgba(16, 185, 129, 0.3)',
                            transform: 'translateY(-2px)'
                          }}
                          _active={{
                            transform: 'translateY(0)'
                          }}
                          transition="all 0.3s ease"
                          isDisabled={isLoading}
                          borderRadius="xl"
                        >
                          <Icon viewBox="0 0 24 24" boxSize={4} mr={2}>
                            <path fill="currentColor" d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M8 2h8v4H8V2" />
                          </Icon>
                          Paste from Clipboard
                        </Button>
                      </VStack>
                      <HStack spacing={3} display={{ base: "none", md: "flex" }}>
                        <InputGroup 
                          size="lg" 
                          flex={1}
                          position="relative"
                          _focusWithin={{
                            boxShadow: '0 0 0 1px #10b981, 0 0 20px rgba(16, 185, 129, 0.4)'
                          }}
                        >
                          <InputLeftElement pointerEvents="none" pl={1}>
                            <Icon viewBox="0 0 24 24" boxSize={5} color="whiteAlpha.400">
                              <rect x="2" y="4" width="20" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                              <path d="M7 8h.01M12 8h.01M17 8h.01M7 12h.01M12 12h.01M17 12h.01M7 16h.01M12 16h.01M17 16h.01" stroke="currentColor" strokeWidth="2" />
                            </Icon>
                          </InputLeftElement>
                          <Input
                            value={uid}
                            onChange={(e) => setUid(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder="Enter your Bitget UID"
                            bg="blackAlpha.400"
                            border="1px solid"
                            borderColor="whiteAlpha.200"
                            _focus={{ 
                              borderColor: 'crypto.green.400',
                              bg: 'blackAlpha.600'
                            }}
                            _hover={{ borderColor: 'whiteAlpha.300' }}
                            color="white"
                            _placeholder={{ color: 'whiteAlpha.400' }}
                            fontSize="md"
                            pattern="[0-9]{1,15}"
                            maxLength="15"
                            inputMode="numeric"
                            isDisabled={isLoading}
                            h="50px"
                            borderRadius="xl"
                            pl="50px"
                          />
                        </InputGroup>
                        <Button
                          h="50px"
                          px={6}
                          onClick={() => pasteText('uid', true)}
                          bg="rgba(255, 255, 255, 0.05)"
                          border="1px solid"
                          borderColor="rgba(255, 255, 255, 0.1)"
                          _hover={{
                            bg: 'rgba(16, 185, 129, 0.1)',
                            borderColor: 'rgba(16, 185, 129, 0.3)',
                            transform: 'translateY(-2px)'
                          }}
                          _active={{
                            transform: 'translateY(0)'
                          }}
                          transition="all 0.3s ease"
                          isDisabled={isLoading}
                          borderRadius="xl"
                        >
                          <Icon viewBox="0 0 24 24" boxSize={4} mr={2}>
                            <path fill="currentColor" d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M8 2h8v4H8V2" />
                          </Icon>
                          Paste
                        </Button>
                      </HStack>
                      <Text mt={2} fontSize="xs" color="whiteAlpha.500">
                        Your UID is a numerical identifier (max 15 digits)
                      </Text>
                    </Box>
                    
                    <Button
                      type="submit"
                      w="full"
                      h={{ base: "48px", md: "50px" }}
                      position="relative"
                      overflow="hidden"
                      bg="linear-gradient(90deg, #0f1f0e, #10b981, #0f1f0e)"
                      backgroundSize="200% 100%"
                      animation={`${shimmer} 3s linear infinite`}
                      color="white"
                      fontWeight="500"
                      borderRadius="0.5rem"
                      isDisabled={isLoading || !uid}
                      isLoading={isLoading}
                      loadingText="Submitting..."
                      fontSize={{ base: "sm", md: "md" }}
                      letterSpacing="-0.01em"
                      _hover={{
                        transform: { base: 'none', md: 'translateY(-2px)' },
                        boxShadow: { base: 'none', md: '0 10px 30px rgba(16, 185, 129, 0.3)' }
                      }}
                      _active={{
                        transform: 'translateY(0)'
                      }}
                    >
                      Submit UID for Verification
                      <Icon viewBox="0 0 24 24" boxSize={{ base: 4, md: 5 }} ml={2}>
                        <path fill="currentColor" d="M5 12h14m-7-7l7 7-7 7" />
                      </Icon>
                    </Button>
                  </VStack>
                </form>
                
                {/* Help section */}
                <Box
                  borderTop="1px solid"
                  borderColor="whiteAlpha.100"
                  pt={4}
                >
                  <HStack
                    justify="space-between"
                    cursor="pointer"
                    py={2}
                    onClick={() => toggleAccordion('uid-help')}
                    _hover={{ color: 'crypto.green.400' }}
                    transition="color 0.2s"
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      Where to find your Bitget UID?
                    </Text>
                    <Icon 
                      viewBox="0 0 24 24" 
                      boxSize={4} 
                      transform={activeAccordion === 'uid-help' ? 'rotate(180deg)' : 'rotate(0deg)'}
                      transition="transform 0.3s"
                    >
                      <path fill="currentColor" d="m6 9 6 6 6-6" />
                    </Icon>
                  </HStack>
                  <Box 
                    maxH={activeAccordion === 'uid-help' ? '500px' : '0'}
                    overflow="hidden"
                    transition="all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)"
                    opacity={activeAccordion === 'uid-help' ? 1 : 0}
                    fontSize="sm" 
                    color="whiteAlpha.600" 
                    py={activeAccordion === 'uid-help' ? 2 : 0}
                  >
                    <OrderedList pl={5} spacing={2}>
                      <ListItem>Log in to your Bitget account</ListItem>
                      <ListItem>Click your profile icon in the top right</ListItem>
                      <ListItem>Select "Personal Center"</ListItem>
                      <ListItem>Your UID is displayed at the top</ListItem>
                    </OrderedList>
                  </Box>
                </Box>
              </VStack>
            </Box>
          )}
          
          {/* Step 2: API Setup */}
          {userStep === 2 && (
            <Box 
              p={{ base: 4, md: 8 }}
              bg="rgba(15, 27, 14, 0.6)"
              backdropFilter="blur(20px)"
              border="1px solid"
              borderColor="rgba(16, 185, 129, 0.2)"
              borderRadius={{ base: "16px", md: "20px" }}
              position="relative"
              overflow="hidden"
              transition="all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)"
              animation={`${slideInUp} 0.8s ease-out`}
              animationDelay="0.3s"
              opacity={0}
              sx={{ animationFillMode: 'forwards' }}
              _hover={{
                transform: { base: 'none', md: 'translateY(-5px)' },
                borderColor: 'rgba(16, 185, 129, 0.4)',
                boxShadow: { base: 'none', md: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(16, 185, 129, 0.2)' }
              }}
            >
              <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                <HStack spacing={{ base: 3, md: 4 }} flexDirection={{ base: 'column', sm: 'row' }} alignItems={{ base: 'center', sm: 'flex-start' }}>
                  <Box
                    w={{ base: 10, md: 12 }}
                    h={{ base: 10, md: 12 }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="full"
                    bg="linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)"
                    position="relative"
                    animation={`${float} 6s ease-in-out infinite`}
                  >
                    <Icon viewBox="0 0 24 24" boxSize={{ base: 5, md: 6 }} color="crypto.green.400">
                      <path fill="currentColor" d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                    </Icon>
                    <Box
                      position="absolute"
                      inset="-1px"
                      borderRadius="full"
                      border="2px solid"
                      borderColor="crypto.green.400"
                      opacity={0.3}
                      animation={`${pulse} 2s infinite`}
                    />
                  </Box>
                  <Box textAlign={{ base: 'center', sm: 'left' }}>
                    <Heading size={{ base: "md", md: "lg" }} fontWeight="semibold" letterSpacing="-0.02em">
                      Bitget API Connection
                    </Heading>
                    <Text color="whiteAlpha.600" fontSize={{ base: "xs", md: "sm" }}>
                      Securely connect your trading account
                    </Text>
                  </Box>
                </HStack>
                
                {/* Permissions notice */}
                <Box
                  p={4}
                  borderRadius="xl"
                  bg="linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(217, 119, 6, 0.05) 100%)"
                  border="1px solid"
                  borderColor="orange.400"
                  borderOpacity={0.3}
                  position="relative"
                  overflow="hidden"
                >
                  <HStack align="start" spacing={3}>
                    <Icon viewBox="0 0 24 24" boxSize={5} color="orange.400" mt={0.5}>
                      <path fill="currentColor" d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3ZM12 9v4m0 4h.01" />
                    </Icon>
                    <Box>
                      <Text fontWeight="semibold" color="orange.300" mb={1}>
                        Required API Permissions
                      </Text>
                      <VStack align="start" spacing={1}>
                        <HStack>
                          <Badge colorScheme="orange" fontSize="xs">Futures Order</Badge>
                          <Text fontSize="sm" color="whiteAlpha.700">Read & Write</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="orange" fontSize="xs">Copy Trading</Badge>
                          <Text fontSize="sm" color="whiteAlpha.700">Read & Write</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="orange" fontSize="xs">Open Interest</Badge>
                          <Text fontSize="sm" color="whiteAlpha.700">Read</Text>
                        </HStack>
                      </VStack>
                    </Box>
                  </HStack>
                </Box>
                
                <form onSubmit={handleApiSubmit}>
                  <VStack spacing={5}>
                    {/* API Key */}
                    <Box w="full">
                      <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium" color="whiteAlpha.800" mb={2}>
                        API Key
                      </Text>
                      <VStack spacing={3} align="stretch" display={{ base: "flex", md: "none" }}>
                        <InputGroup 
                          size="lg" 
                          flex={1}
                          position="relative"
                          _focusWithin={{
                            boxShadow: '0 0 0 1px #10b981, 0 0 20px rgba(16, 185, 129, 0.4)'
                          }}
                        >
                          <InputLeftElement pointerEvents="none" pl={1}>
                            <Icon viewBox="0 0 24 24" boxSize={5} color="whiteAlpha.400">
                              <path fill="currentColor" d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                            </Icon>
                          </InputLeftElement>
                          <Input
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            placeholder="Enter your API Key"
                            bg="blackAlpha.400"
                            border="1px solid"
                            borderColor="whiteAlpha.200"
                            _focus={{ 
                              borderColor: 'crypto.green.400',
                              bg: 'blackAlpha.600'
                            }}
                            _hover={{ borderColor: 'whiteAlpha.300' }}
                            color="white"
                            _placeholder={{ color: 'whiteAlpha.400' }}
                            fontSize="md"
                            isDisabled={isLoading}
                            h="48px"
                            borderRadius="xl"
                            pl="50px"
                          />
                        </InputGroup>
                        <Button
                          w="full"
                          h="44px"
                          onClick={() => pasteText('api_key')}
                          bg="rgba(255, 255, 255, 0.05)"
                          border="1px solid"
                          borderColor="rgba(255, 255, 255, 0.1)"
                          _hover={{
                            bg: 'rgba(16, 185, 129, 0.1)',
                            borderColor: 'rgba(16, 185, 129, 0.3)',
                            transform: 'translateY(-2px)'
                          }}
                          _active={{
                            transform: 'translateY(0)'
                          }}
                          transition="all 0.3s ease"
                          isDisabled={isLoading}
                          borderRadius="xl"
                        >
                          <Icon viewBox="0 0 24 24" boxSize={4} mr={2}>
                            <path fill="currentColor" d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M8 2h8v4H8V2" />
                          </Icon>
                          Paste API Key
                        </Button>
                      </VStack>
                      <HStack spacing={3} display={{ base: "none", md: "flex" }}>
                        <InputGroup 
                          size="lg" 
                          flex={1}
                          position="relative"
                          _focusWithin={{
                            boxShadow: '0 0 0 1px #10b981, 0 0 20px rgba(16, 185, 129, 0.4)'
                          }}
                        >
                          <InputLeftElement pointerEvents="none" pl={1}>
                            <Icon viewBox="0 0 24 24" boxSize={5} color="whiteAlpha.400">
                              <path fill="currentColor" d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                            </Icon>
                          </InputLeftElement>
                          <Input
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            placeholder="Enter your API Key"
                            bg="blackAlpha.400"
                            border="1px solid"
                            borderColor="whiteAlpha.200"
                            _focus={{ 
                              borderColor: 'crypto.green.400',
                              bg: 'blackAlpha.600'
                            }}
                            _hover={{ borderColor: 'whiteAlpha.300' }}
                            color="white"
                            _placeholder={{ color: 'whiteAlpha.400' }}
                            fontSize="md"
                            isDisabled={isLoading}
                            h="50px"
                            borderRadius="xl"
                            pl="50px"
                          />
                        </InputGroup>
                        <Button
                          h="50px"
                          px={6}
                          onClick={() => pasteText('api_key')}
                          bg="rgba(255, 255, 255, 0.05)"
                          border="1px solid"
                          borderColor="rgba(255, 255, 255, 0.1)"
                          _hover={{
                            bg: 'rgba(16, 185, 129, 0.1)',
                            borderColor: 'rgba(16, 185, 129, 0.3)',
                            transform: 'translateY(-2px)'
                          }}
                          _active={{
                            transform: 'translateY(0)'
                          }}
                          transition="all 0.3s ease"
                          isDisabled={isLoading}
                          borderRadius="xl"
                        >
                          <Icon viewBox="0 0 24 24" boxSize={4} mr={2}>
                            <path fill="currentColor" d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M8 2h8v4H8V2" />
                          </Icon>
                          Paste
                        </Button>
                      </HStack>
                    </Box>
                    
                    {/* API Secret */}
                    <Box w="full">
                      <Text fontSize="sm" fontWeight="medium" color="whiteAlpha.800" mb={2}>
                        API Secret
                      </Text>
                      <HStack spacing={3}>
                        <InputGroup 
                          size="lg" 
                          flex={1}
                          position="relative"
                          _focusWithin={{
                            boxShadow: '0 0 0 1px #10b981, 0 0 20px rgba(16, 185, 129, 0.4)'
                          }}
                        >
                          <InputLeftElement pointerEvents="none" pl={1}>
                            <Icon viewBox="0 0 24 24" boxSize={5} color="whiteAlpha.400">
                              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" fill="none" stroke="currentColor" strokeWidth="2" />
                            </Icon>
                          </InputLeftElement>
                          <Input
                            type={showSecrets.secret ? "text" : "password"}
                            value={apiSecret}
                            onChange={(e) => setApiSecret(e.target.value)}
                            placeholder="Enter your API Secret"
                            bg="blackAlpha.400"
                            border="1px solid"
                            borderColor="whiteAlpha.200"
                            _focus={{ 
                              borderColor: 'crypto.green.400',
                              bg: 'blackAlpha.600'
                            }}
                            _hover={{ borderColor: 'whiteAlpha.300' }}
                            color="white"
                            _placeholder={{ color: 'whiteAlpha.400' }}
                            fontSize="md"
                            isDisabled={isLoading}
                            h="50px"
                            borderRadius="xl"
                            pl="50px"
                            pr="50px"
                          />
                          <Box position="absolute" right={3} top="50%" transform="translateY(-50%)" zIndex={2}>
                            <Icon
                              viewBox="0 0 24 24"
                              boxSize={5}
                              color="whiteAlpha.400"
                              cursor="pointer"
                              onClick={() => setShowSecrets(prev => ({ ...prev, secret: !prev.secret }))}
                              _hover={{ color: 'crypto.green.400' }}
                            >
                              {showSecrets.secret ? (
                                <path fill="currentColor" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                              ) : (
                                <path fill="currentColor" d="m22.54 16.88-1.42-1.41A9.36 9.36 0 0 0 23 12s-4-8-11-8a9.28 9.28 0 0 0-4 .87L5.71 2.58 4.29 4l18 18 1.42-1.41-1.17-1.18v.01zM12 9a3 3 0 0 1 3 3 3 3 0 0 1-.17 1L11 9.17A3 3 0 0 1 12 9zm0 8c-1.33 0-2.55-.3-3.66-.77l1.48-1.48c.66.17 1.39.25 2.18.25 3.66 0 6.3-2.43 7.43-4.28a10.15 10.15 0 0 0-1.29-1.73L16.73 7.6A9.36 9.36 0 0 1 21 12s-4 8-9 8z" />
                              )}
                            </Icon>
                          </Box>
                        </InputGroup>
                        <Button
                          h="50px"
                          px={6}
                          onClick={() => pasteText('api_secret')}
                          bg="rgba(255, 255, 255, 0.05)"
                          border="1px solid"
                          borderColor="rgba(255, 255, 255, 0.1)"
                          _hover={{
                            bg: 'rgba(16, 185, 129, 0.1)',
                            borderColor: 'rgba(16, 185, 129, 0.3)',
                            transform: 'translateY(-2px)'
                          }}
                          _active={{
                            transform: 'translateY(0)'
                          }}
                          transition="all 0.3s ease"
                          isDisabled={isLoading}
                          borderRadius="xl"
                        >
                          <Icon viewBox="0 0 24 24" boxSize={4} mr={2}>
                            <path fill="currentColor" d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M8 2h8v4H8V2" />
                          </Icon>
                          Paste
                        </Button>
                      </HStack>
                    </Box>
                    
                    {/* API Passphrase */}
                    <Box w="full">
                      <Text fontSize="sm" fontWeight="medium" color="whiteAlpha.800" mb={2}>
                        API Passphrase
                      </Text>
                      <HStack spacing={3}>
                        <InputGroup 
                          size="lg" 
                          flex={1}
                          position="relative"
                          _focusWithin={{
                            boxShadow: '0 0 0 1px #10b981, 0 0 20px rgba(16, 185, 129, 0.4)'
                          }}
                        >
                          <InputLeftElement pointerEvents="none" pl={1}>
                            <Icon viewBox="0 0 24 24" boxSize={5} color="whiteAlpha.400">
                              <path fill="currentColor" d="M2 18v3c0 .6.4 1 1 1h4v-3h3v-3h2l1.4-1.4a6.5 6.5 0 1 0-4-4ZM16.5 8a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1" />
                            </Icon>
                          </InputLeftElement>
                          <Input
                            type={showSecrets.passphrase ? "text" : "password"}
                            value={apiPassphrase}
                            onChange={(e) => setApiPassphrase(e.target.value)}
                            placeholder="Enter your API Passphrase"
                            bg="blackAlpha.400"
                            border="1px solid"
                            borderColor="whiteAlpha.200"
                            _focus={{ 
                              borderColor: 'crypto.green.400',
                              bg: 'blackAlpha.600'
                            }}
                            _hover={{ borderColor: 'whiteAlpha.300' }}
                            color="white"
                            _placeholder={{ color: 'whiteAlpha.400' }}
                            fontSize="md"
                            isDisabled={isLoading}
                            h="50px"
                            borderRadius="xl"
                            pl="50px"
                            pr="50px"
                          />
                          <Box position="absolute" right={3} top="50%" transform="translateY(-50%)" zIndex={2}>
                            <Icon
                              viewBox="0 0 24 24"
                              boxSize={5}
                              color="whiteAlpha.400"
                              cursor="pointer"
                              onClick={() => setShowSecrets(prev => ({ ...prev, passphrase: !prev.passphrase }))}
                              _hover={{ color: 'crypto.green.400' }}
                            >
                              {showSecrets.passphrase ? (
                                <path fill="currentColor" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                              ) : (
                                <path fill="currentColor" d="m22.54 16.88-1.42-1.41A9.36 9.36 0 0 0 23 12s-4-8-11-8a9.28 9.28 0 0 0-4 .87L5.71 2.58 4.29 4l18 18 1.42-1.41-1.17-1.18v.01zM12 9a3 3 0 0 1 3 3 3 3 0 0 1-.17 1L11 9.17A3 3 0 0 1 12 9zm0 8c-1.33 0-2.55-.3-3.66-.77l1.48-1.48c.66.17 1.39.25 2.18.25 3.66 0 6.3-2.43 7.43-4.28a10.15 10.15 0 0 0-1.29-1.73L16.73 7.6A9.36 9.36 0 0 1 21 12s-4 8-9 8z" />
                              )}
                            </Icon>
                          </Box>
                        </InputGroup>
                        <Button
                          h="50px"
                          px={6}
                          onClick={() => pasteText('api_passphrase')}
                          bg="rgba(255, 255, 255, 0.05)"
                          border="1px solid"
                          borderColor="rgba(255, 255, 255, 0.1)"
                          _hover={{
                            bg: 'rgba(16, 185, 129, 0.1)',
                            borderColor: 'rgba(16, 185, 129, 0.3)',
                            transform: 'translateY(-2px)'
                          }}
                          _active={{
                            transform: 'translateY(0)'
                          }}
                          transition="all 0.3s ease"
                          isDisabled={isLoading}
                          borderRadius="xl"
                        >
                          <Icon viewBox="0 0 24 24" boxSize={4} mr={2}>
                            <path fill="currentColor" d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M8 2h8v4H8V2" />
                          </Icon>
                          Paste
                        </Button>
                      </HStack>
                    </Box>
                    
                    <Button
                      type="submit"
                      w="full"
                      h={{ base: "48px", md: "50px" }}
                      position="relative"
                      overflow="hidden"
                      bg="linear-gradient(90deg, #0f1f0e, #10b981, #0f1f0e)"
                      backgroundSize="200% 100%"
                      animation={`${shimmer} 3s linear infinite`}
                      color="white"
                      fontWeight="500"
                      borderRadius="0.5rem"
                      isDisabled={isLoading || !apiKey || !apiSecret || !apiPassphrase}
                      isLoading={isLoading}
                      loadingText="Verifying Connection..."
                      fontSize={{ base: "sm", md: "md" }}
                      letterSpacing="-0.01em"
                      _hover={{
                        transform: { base: 'none', md: 'translateY(-2px)' },
                        boxShadow: { base: 'none', md: '0 10px 30px rgba(16, 185, 129, 0.3)' }
                      }}
                      _active={{
                        transform: 'translateY(0)'
                      }}
                    >
                      Complete Setup
                      <Icon viewBox="0 0 24 24" boxSize={{ base: 4, md: 5 }} ml={2}>
                        <path fill="currentColor" d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3" />
                      </Icon>
                    </Button>
                  </VStack>
                </form>
                
                {/* Help section */}
                <Box
                  borderTop="1px solid"
                  borderColor="whiteAlpha.100"
                  pt={4}
                >
                  <HStack
                    justify="space-between"
                    cursor="pointer"
                    py={2}
                    onClick={() => toggleAccordion('api-help')}
                    _hover={{ color: 'crypto.green.400' }}
                    transition="color 0.2s"
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      How to create a Bitget API Key?
                    </Text>
                    <Icon 
                      viewBox="0 0 24 24" 
                      boxSize={4} 
                      transform={activeAccordion === 'api-help' ? 'rotate(180deg)' : 'rotate(0deg)'}
                      transition="transform 0.3s"
                    >
                      <path fill="currentColor" d="m6 9 6 6 6-6" />
                    </Icon>
                  </HStack>
                  <Box 
                    maxH={activeAccordion === 'api-help' ? '500px' : '0'}
                    overflow="hidden"
                    transition="all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)"
                    opacity={activeAccordion === 'api-help' ? 1 : 0}
                    fontSize="sm" 
                    color="whiteAlpha.600" 
                    py={activeAccordion === 'api-help' ? 2 : 0}
                  >
                    <OrderedList pl={5} spacing={2}>
                      <ListItem>Log in to your Bitget account</ListItem>
                      <ListItem>Navigate to "API Management"</ListItem>
                      <ListItem>Click "Create API"</ListItem>
                      <ListItem>Name your API (e.g., "Wells Trading")</ListItem>
                      <ListItem>Enable required permissions</ListItem>
                      <ListItem>Complete security verification</ListItem>
                      <ListItem>Save your credentials securely</ListItem>
                    </OrderedList>
                  </Box>
                </Box>
              </VStack>
            </Box>
          )}
          
          {/* FAQ Section */}
          <VStack spacing={3} mt={{ base: 6, md: 8 }} animation={`${slideInUp} 0.8s ease-out`} animationDelay="0.4s" opacity={0} sx={{ animationFillMode: 'forwards' }}>
            <Heading size={{ base: "sm", md: "md" }} alignSelf="flex-start" color="whiteAlpha.800" mb={2}>
              Frequently Asked Questions
            </Heading>
            
            {[
              {
                id: 'faq-1',
                question: 'How long does verification take?',
                answer: 'UID verification typically takes 24-48 hours. We\'ll notify you by email once your account is verified, and this page will automatically update.'
              },
              {
                id: 'faq-2',
                question: 'Why is verification required?',
                answer: 'Verification ensures your account meets our trading requirements and was registered with our referral code. This helps maintain security and quality standards.'
              },
              {
                id: 'faq-3',
                question: 'Is my API information secure?',
                answer: 'Yes, your API credentials are encrypted and stored securely. We never have access to withdraw funds - only to execute trades on your behalf.'
              },
              {
                id: 'faq-4',
                question: 'What if I need help?',
                answer: 'Our support team is available 24/7. You can reach us through the support section or email support@wells.finance.'
              }
            ].map((faq) => (
              <Box
                key={faq.id}
                w="full"
                borderRadius={{ base: "lg", md: "xl" }}
                overflow="hidden"
                bg="whiteAlpha.50"
                border="1px solid"
                borderColor="whiteAlpha.100"
                transition="all 0.3s"
                _hover={{ borderColor: 'whiteAlpha.200', bg: 'whiteAlpha.100' }}
              >
                <HStack
                  justify="space-between"
                  cursor="pointer"
                  p={{ base: 4, md: 5 }}
                  onClick={() => toggleAccordion(faq.id)}
                >
                  <HStack spacing={{ base: 2, md: 3 }}>
                    <Icon viewBox="0 0 24 24" boxSize={{ base: 4, md: 5 }} color="crypto.green.400">
                      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" />
                      <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" />
                    </Icon>
                    <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium">{faq.question}</Text>
                  </HStack>
                  <Icon 
                    viewBox="0 0 24 24" 
                    boxSize={4} 
                    color="whiteAlpha.500"
                    transform={activeAccordion === faq.id ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition="transform 0.3s"
                  >
                    <path fill="currentColor" d="m6 9 6 6 6-6" />
                  </Icon>
                </HStack>
                <Box 
                  maxH={activeAccordion === faq.id ? '500px' : '0'}
                  overflow="hidden"
                  transition="all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)"
                  opacity={activeAccordion === faq.id ? 1 : 0}
                  px={{ base: 4, md: 5 }}
                  pb={activeAccordion === faq.id ? { base: 4, md: 5 } : 0}
                >
                  <Text fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.600" pl={{ base: 6, md: 8 }}>
                    {faq.answer}
                  </Text>
                </Box>
              </Box>
            ))}
          </VStack>
        </Box>
      </Box>
      
      {/* Footer */}
      <Box py={{ base: 6, md: 8 }} borderTop="1px solid" borderColor="whiteAlpha.100" position="relative" zIndex={1}>
        <Box maxW="container.lg" mx="auto" px={{ base: 4, md: 6 }}>
          <VStack spacing={{ base: 3, md: 4 }}>
            <HStack spacing={{ base: 2, md: 3 }}>
              <Box
                w={{ base: 6, md: 8 }}
                h={{ base: 6, md: 8 }}
                bgGradient="linear(135deg, rgb(40 185 16), rgb(24 79 38))"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                animation={`${float} 6s ease-in-out infinite`}
              >
                <img 
                  src="/assets/light_logo.png" 
                  alt="Wells" 
                  style={{
                    width: '20px',
                    height: '20px',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" letterSpacing="-0.02em">
                Wells<Text as="span" color="crypto.green.400">.</Text>
              </Text>
            </HStack>
            
            <HStack spacing={{ base: 4, md: 6 }}>
              <Text as="a" href="#" fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.600" _hover={{ color: "crypto.green.400" }}>
                Terms
              </Text>
              <Text as="a" href="#" fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.600" _hover={{ color: "crypto.green.400" }}>
                Privacy
              </Text>
              <Text as="a" href="#" fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.600" _hover={{ color: "crypto.green.400" }}>
                Support
              </Text>
            </HStack>
            
            <Text fontSize={{ base: "2xs", md: "xs" }} color="whiteAlpha.500">
              © {new Date().getFullYear()} Wells AI. All rights reserved.
            </Text>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default AccountSetup;